import 'primeflex/primeflex.css';
import 'primeicons/primeicons.css';
import './InstallerPanelTable.css';
import "primereact/resources/themes/lara-light-indigo/theme.css";
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { exportDataTableCsv } from '../../helpers/exportCsv';
import { Toolbar } from 'primereact/toolbar';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Card } from 'primereact/card';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { FilterMatchMode } from 'primereact/api';
import { DataView } from 'primereact/dataview';
import Snackbar from '../../../components/Snackbar/Snackbar';
import { ISystem } from '../../helpers/interfaces';
import { connect } from 'react-redux';
import { getSystemsVeryShort, getAgreements, updateAgreement, closeUpdateAgreementSuccessNotification, closeUpdateAgreementFailNotification, 
         addAgreement, closeCreateAgreementSuccessNotification, closeCreateAgreementFailNotification, deleteAgreement, getClients } from '../../stores/actions/installerActions';
import { arrayIncludes } from '@material-ui/pickers/_helpers/utils';
import { useTopScrollbar } from '../../helpers/useTopScrollbar';

interface AgreementsProps {
	systems: ISystem[];
	mobileView: boolean;
    dbAgreements: any,
    updateAgreementSuccess: boolean,
    updateAgreementFail: boolean,
    createAgreementSuccess: boolean,
    createAgreementFail: boolean,
	getSystemsVeryShort: () => void;
    getAgreements: () => void;
    updateAgreement: (agreement) => void;
    addAgreement: (agreement) => void;
    deleteAgreement: (systemId) => void;
    closeUpdateAgreementSuccessNotification: () => void;
    closeUpdateAgreementFailNotification: () => void;
    closeCreateAgreementSuccessNotification: () => void;
    closeCreateAgreementFailNotification: () => void;
    clients: { ID: number; name: string }[] | null;
    getClients: () => void;
}

const Agreements = ({
	systems,
	mobileView,
    dbAgreements,
    getAgreements,
    getSystemsVeryShort,
    updateAgreement,
    addAgreement,
    deleteAgreement,
    updateAgreementSuccess,
    updateAgreementFail,
    createAgreementSuccess,
    createAgreementFail,
    closeUpdateAgreementSuccessNotification,
    closeUpdateAgreementFailNotification,
    closeCreateAgreementSuccessNotification,
    closeCreateAgreementFailNotification,
    clients,
    getClients
}: AgreementsProps) => {
    let emptyAgreement = {
        ID: null,
        systemId: null,
        systemName: null,
        installationAgreementNumber: null,
        installationAgreementDate: new Date(),
        supportAgreementNumber: null,
        supportAgreementDate: new Date(),
        supportAgreementEndDate: new Date(),
        guaranteeAgreementNumber: null,
        guaranteeAgreementDate: new Date(),
        guaranteeAgreementEndDate: new Date(),
        handoverDate: new Date(),
        baseGuaranteeTime: null,
        maintenanceCoveredByGuarantee: false,
        installationAgreementClientId: null,
        guaranteeAgreementClientId: null,
        supportAgreementClientId: null
    };

    useEffect(() => {
        getSystemsVeryShort();
        getClients();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (systems && dbAgreements) {
            const systemNamesByIds = Object.fromEntries(systems.map(x => [x.ID, x.name]));
            const clientNamesByIds = Object.fromEntries((Array.isArray(clients) ? clients : []).map(x => [x.ID, x.name]));
            // names kept on rows so that the table can sort, filter and export them
            setAgreements(dbAgreements.map(agr => ({
                ...agr,
                systemName: systemNamesByIds[agr.systemId],
                installationAgreementClientName: clientNamesByIds[agr.installationAgreementClientId] ?? null,
                guaranteeAgreementClientName: clientNamesByIds[agr.guaranteeAgreementClientId] ?? null,
                supportAgreementClientName: clientNamesByIds[agr.supportAgreementClientId] ?? null,
            })));
        }

	}, [dbAgreements, systems, clients]);

	useEffect(() => {
		if (updateAgreementSuccess) {
            setTimeout(() => {
                closeUpdateAgreementSuccessNotification();
            }, 6000);
            setAgreementDialog(false);
		}
	}, [updateAgreementSuccess]);

    useEffect(() => {
		if (updateAgreementFail) {
            setTimeout(() => {
                closeUpdateAgreementFailNotification();
            }, 6000)
        }
	}, [updateAgreementFail]);


    useEffect(() => {
		if (createAgreementSuccess) {
            setTimeout(() => {
                closeCreateAgreementSuccessNotification();
            }, 6000);
			setAgreementDialog(false);
		}
	}, [createAgreementSuccess]);

    useEffect(() => {
		if (createAgreementFail) {
            setTimeout(() => {
                closeCreateAgreementFailNotification();
            }, 6000)
        }
	}, [createAgreementFail]);

    useEffect(() => {
        if (systems) {
            getAgreements();
        }
	}, [systems]);

    const dt = useRef(null);


    const [agreementDialog, setAgreementDialog] = useState(false);
    const [deleteAgreementDialog, setDeleteAgreementDialog] = useState(false);
    const [globalFilter, setGlobalFilter] = useState(null);
    const [agreements, setAgreements] = useState(dbAgreements);
    const { tableWrapperRef, topScrollRef, topScrollContentRef } = useTopScrollbar([agreements, mobileView]);
    const [agreement, setAgreement] = useState(emptyAgreement);
    const today = new Date();
    today.setHours(0,0,0,0);

    const [filters, setFilters] = useState({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
        systemName: { value: null, matchMode: FilterMatchMode.CONTAINS },
        installationAgreementClientName: { value: null, matchMode: FilterMatchMode.CONTAINS },
        guaranteeAgreementClientName: { value: null, matchMode: FilterMatchMode.CONTAINS },
        supportAgreementClientName: { value: null, matchMode: FilterMatchMode.CONTAINS }
    });

    const [mobileFilterDialog, setMobileFilterDialog] = useState(false);
    const [mobileFilters, setMobileFilters] = useState({
        systemName: '',
        installationAgreementNumber: '',
        guaranteeAgreementNumber: '',
        supportAgreementNumber: '',
        expiredGuarantee: null,
        expiredSupport: null,
    });
    const [mobileSortField, setMobileSortField] = useState('ID');
    const [mobileSortOrder, setMobileSortOrder] = useState<-1 | 1>(-1);
    const [pendingFilters, setPendingFilters] = useState(mobileFilters);
    const [pendingSortField, setPendingSortField] = useState(mobileSortField);
    const [pendingSortOrder, setPendingSortOrder] = useState<-1 | 1>(mobileSortOrder);

    const openMobileFilterDialog = () => {
        setPendingFilters(mobileFilters);
        setPendingSortField(mobileSortField);
        setPendingSortOrder(mobileSortOrder);
        setMobileFilterDialog(true);
    };

    const applyMobileFilters = () => {
        setMobileFilters(pendingFilters);
        setMobileSortField(pendingSortField);
        setMobileSortOrder(pendingSortOrder);
        setMobileFilterDialog(false);
    };

    const resetMobileFilters = () => {
        const empty = {
            systemName: '',
            installationAgreementNumber: '',
            guaranteeAgreementNumber: '',
            supportAgreementNumber: '',
            expiredGuarantee: null,
            expiredSupport: null,
        };
        setPendingFilters(empty);
        setPendingSortField('ID');
        setPendingSortOrder(-1);
    };

    const filteredAgreements = useMemo(() => {
        if (!agreements) return [];
        let result = [...agreements];

        if (mobileFilters.systemName)
            result = result.filter(a => a.systemName?.toLowerCase().includes(mobileFilters.systemName.toLowerCase()));
        if (mobileFilters.installationAgreementNumber)
            result = result.filter(a => a.installationAgreementNumber?.toLowerCase().includes(mobileFilters.installationAgreementNumber.toLowerCase()));
        if (mobileFilters.guaranteeAgreementNumber)
            result = result.filter(a => a.guaranteeAgreementNumber?.toLowerCase().includes(mobileFilters.guaranteeAgreementNumber.toLowerCase()));
        if (mobileFilters.supportAgreementNumber)
            result = result.filter(a => a.supportAgreementNumber?.toLowerCase().includes(mobileFilters.supportAgreementNumber.toLowerCase()));
        if (mobileFilters.expiredGuarantee === true)
            result = result.filter(a => a.guaranteeAgreementEndDate && new Date(a.guaranteeAgreementEndDate) < today);
        if (mobileFilters.expiredGuarantee === false)
            result = result.filter(a => !a.guaranteeAgreementEndDate || new Date(a.guaranteeAgreementEndDate) >= today);
        if (mobileFilters.expiredSupport === true)
            result = result.filter(a => a.supportAgreementEndDate && new Date(a.supportAgreementEndDate) < today);
        if (mobileFilters.expiredSupport === false)
            result = result.filter(a => !a.supportAgreementEndDate || new Date(a.supportAgreementEndDate) >= today);

        result.sort((a, b) => {
            const aVal = a[mobileSortField] ?? '';
            const bVal = b[mobileSortField] ?? '';
            if (aVal < bVal) return -1 * mobileSortOrder;
            if (aVal > bVal) return 1 * mobileSortOrder;
            return 0;
        });

        return result;
    }, [agreements, mobileFilters, mobileSortField, mobileSortOrder]);

    const activeFilterCount = useMemo(() => {
        return Object.entries(mobileFilters).filter(([key, v]) => {
            if (key === 'systemName') return false;
            return v !== '' && v !== null;
        }).length;
    }, [mobileFilters]);

    const sortOptions = [
        { label: 'ID', value: 'ID' },
        { label: 'System', value: 'systemName' },
        { label: 'IA Number', value: 'installationAgreementNumber' },
        { label: 'IA Date', value: 'installationAgreementDate' },
        { label: 'Handover', value: 'handoverDate' },
        { label: 'GA Number', value: 'guaranteeAgreementNumber' },
        { label: 'GA Date', value: 'guaranteeAgreementDate' },
        { label: 'GA End Date', value: 'guaranteeAgreementEndDate' },
        { label: 'GA Time', value: 'baseGuaranteeTime' },
        { label: 'SA Number', value: 'supportAgreementNumber' },
        { label: 'SA Date', value: 'supportAgreementDate' },
        { label: 'SA End Date', value: 'supportAgreementEndDate' },
    ];



    const pastEndDateStyle = {backgroundColor: 'gold', padding: '6px', borderRadius: '6px', textAlign: 'center'};
    const dateStyle = {textAlign: 'center'};
    let systemNames = [];
    if (systems && dbAgreements) {
        const takenSystemIds = dbAgreements.map(el => el.systemId);
        const freeSystems = systems.filter((val) => !arrayIncludes(takenSystemIds, val.ID));
        systemNames = (freeSystems.map(el => el.name).sort());
    }

    const clientOptions = (Array.isArray(clients) ? clients : []).map((client) => ({ label: client.name, value: client.ID }));

    const openNew = () => {
        setAgreement(emptyAgreement);
        setAgreementDialog(true);
    };

    const hideDialog = () => {
        setAgreementDialog(false);
    };

    const hideDeleteAgreementDialog = () => {
        setDeleteAgreementDialog(false);
    };

    const saveAgreement = () => {
        if (!agreement.systemName) {
            return;
        }
        const _agreement = { ...agreement };
        // system can't be changed when editing - keep its id
        if (!_agreement.ID) {
            const systemIdsByNames = Object.fromEntries(systems.map(x => [x.name, x.ID]));
            _agreement.systemId = systemIdsByNames[_agreement.systemName] ?? 0;
        }
        if (_agreement.ID) {
            updateAgreement(_agreement);
        } else {
            addAgreement(_agreement);
        }
    };

    const editAgreement = (agreement) => {
        setAgreement({ ...agreement });
        setAgreementDialog(true);
    };

    const confirmDeleteAgreement = (agreement) => {
        setAgreement(agreement);
        setDeleteAgreementDialog(true);
    };

    const deleteAgreementItem = () => {
        deleteAgreement(agreement.ID);
        let _agreements = agreements.filter((val) => val.ID !== agreement.ID);
        setAgreements(_agreements);
        setDeleteAgreementDialog(false);
        setAgreement(emptyAgreement);
    };

    const onInputChange = (e, name) => {
        const val = (e.target && e.target.value);
        let _agreement = { ...agreement };
        if (arrayIncludes(['installationAgreementDate', 'supportAgreementDate', 'supportAgreementEndDate', 'guaranteeAgreementDate',
            'guaranteeAgreementEndDate', 'handoverDate'], name) && val) {
            val.setMinutes(val.getMinutes() - val.getTimezoneOffset());
        } 
        if (name == 'baseGuaranteeTime') {
            // InputText's onChange has no e.value, only e.target.value
            _agreement[`${name}`] = val ? parseInt(val, 10) : null;
        } else if (name.endsWith('ClientId')) {
            _agreement[`${name}`] = e.value;
        } else {
            _agreement[`${name}`] = val;
        }
        
        setAgreement(_agreement);
    };

    const installationAgreementDateTemplate = (rowData) => {
        if (rowData.installationAgreementDate) {
            return (new Date(rowData.installationAgreementDate)).toLocaleDateString('sv-SE');
        } 
        return null;
    };
    const supportAgreementDateTemplate = (rowData) => {
        if (rowData.supportAgreementDate) {
            return (new Date(rowData.supportAgreementDate)).toLocaleDateString('sv-SE');
        } 
        return null;
    };
    const supportAgreementEndDateTemplate = (rowData) => {
        if (rowData.supportAgreementEndDate) {
            let date = (new Date(rowData.supportAgreementEndDate)).toLocaleDateString('sv-SE');
            if (new Date(rowData.supportAgreementEndDate) < today) {
                return <div style={pastEndDateStyle}>{date}</div>
            }
            return <div style={dateStyle}>{date}</div>;
        } 
        return null;
    };
    const guaranteeAgreementDateTemplate = (rowData) => {
        if (rowData.guaranteeAgreementDate) {
            return (new Date(rowData.guaranteeAgreementDate)).toLocaleDateString('sv-SE');
        } 
        return null;
    };
    const guaranteeAgreementEndDateTemplate = (rowData) => {
        if (rowData.guaranteeAgreementEndDate) {
            let date = (new Date(rowData.guaranteeAgreementEndDate)).toLocaleDateString('sv-SE');
            if (new Date(rowData.guaranteeAgreementEndDate) < today) {
                return <div style={pastEndDateStyle}>{date}</div>
            }
            return <div style={dateStyle}>{date}</div>;
        } 
        return null;
    };
    const handoverDateTemplate = (rowData) => {
        if (rowData.handoverDate) {
            return (new Date(rowData.handoverDate)).toLocaleDateString('sv-SE');
        } 
        return null;
    };

    const systemNameTemplate = (rowData) => {
        if (rowData.systemName) {
            return <div>{rowData.systemName}</div>;
        } 
        return '??';
    };

    const mobileFilterBar = (
        <div className="flex align-items-center gap-2 mb-3">
            <span className="p-input-icon-left flex-1">
                <InputText
                    value={mobileFilters.systemName}
                    onChange={(e) => setMobileFilters(prev => ({ ...prev, systemName: e.target.value }))}
                    placeholder="Search system..."
                    className="w-full"
                />
            </span>
            <div className="relative">
                <Button
                    icon="pi pi-filter"
                    rounded
                    outlined={activeFilterCount === 0}
                    severity={activeFilterCount > 0 ? 'info' : undefined}
                    onClick={openMobileFilterDialog}
                />
                {activeFilterCount > 0 && (
                    <span
                        className="absolute top-0 right-0 bg-primary text-white border-circle flex align-items-center justify-content-center"
                        style={{ width: '1.2rem', height: '1.2rem', fontSize: '0.65rem', transform: 'translate(30%, -30%)' }}
                    >
                        {activeFilterCount}
                    </span>
                )}
            </div>
            <Button icon="pi pi-plus" severity="success" rounded onClick={openNew} />
        </div>
    );

    const cardTemplate = (rowData) => {
        return (
            <div className="col-12">
                <div className="border-1 surface-border border-round p-3 mb-2 surface-card">

                    <div className="flex justify-content-between align-items-center pb-2 mb-3 border-bottom-1 surface-border">
                        <span className="font-bold text-lg">{rowData.ID}</span>
                    </div>
                    <div className="mb-3 p-2 surface-50 border-round">
                        <div className="grid">
                            <div className="col-11">
                                <div className="text-xs text-color-secondary">System</div>
                                <div className="font-medium">{rowData.systemName || '---'}</div>
                            </div>
                            <div className="col-1"></div>
                            <div className="col-1"></div>
                        </div>
                    </div>
                    <div className="mb-3 p-2 surface-50 border-round">
                        <div className="text-xs font-semibold text-color-secondary uppercase mb-2">
                            Installation Agreement
                        </div>
                        <div className="grid">
                            <div className="col-5">
                                <div className="text-xs text-color-secondary">Number</div>
                                <div className="font-medium" style={{ wordBreak: 'break-word' }}>
                                    {rowData.installationAgreementNumber || '---'}
                                </div>
                            </div>
                            <div className="col-2"></div>
                            <div className="col-5">
                                <div className="text-xs text-color-secondary">Date</div>
                                <div className="font-medium">
                                    {installationAgreementDateTemplate(rowData)}
                                </div>
                            </div>
                            <div className="col-12">
                                <div className="text-xs text-color-secondary">Client</div>
                                <div className="font-medium" style={{ wordBreak: 'break-word' }}>{rowData.installationAgreementClientName || '---'}</div>
                            </div>
                        </div>
                    </div>
                    <div className="mb-3 p-2 surface-50 border-round">
                        <div className="text-xs font-semibold text-color-secondary uppercase mb-2">
                            Guarantee Agreement
                        </div>
                        <div className="grid">
                            <div className="col-5">
                                <div className="text-xs text-color-secondary">Number</div>
                                <div className="font-medium" style={{ wordBreak: 'break-word' }}>{rowData.guaranteeAgreementNumber || '---'}</div>
                            </div>
                            <div className="col-2"></div>
                            <div className="col-5">
                                <div className="text-xs text-color-secondary">Time</div>
                                <div className="font-medium">{rowData.baseGuaranteeTime || '---'}</div>
                            </div>
                            <div className="col-5">
                                <div className="text-xs text-color-secondary">Start</div>
                                <div className="font-medium">{guaranteeAgreementDateTemplate(rowData)}</div>
                            </div>
                            <div className="col-2"></div>
                            <div className="col-5">
                                <div className="text-xs text-color-secondary">End</div>
                                <div className="font-medium">{guaranteeAgreementEndDateTemplate(rowData)}</div>
                            </div>
                            <div className="col-12">
                                <div className="text-xs text-color-secondary">Client</div>
                                <div className="font-medium" style={{ wordBreak: 'break-word' }}>{rowData.guaranteeAgreementClientName || '---'}</div>
                            </div>
                        </div>
                    </div>

                    <div className="mb-3 p-2 surface-50 border-round">
                        <div className="text-xs font-semibold text-color-secondary uppercase mb-2">
                            Support Agreement
                        </div>
                        <div className="grid">
                            <div className="col-5">
                                <div className="text-xs text-color-secondary" style={{ wordBreak: 'break-word' }}>Number</div>
                                <div className="font-medium">{rowData.supportAgreementNumber || '---'}</div>
                            </div>
                        </div>
                        <div className="grid">
                            <div className="col-5">
                                <div className="text-xs text-color-secondary">Start</div>
                                <div className="font-medium">{supportAgreementDateTemplate(rowData)}</div>
                            </div>
                            <div className="col-2"></div>
                            <div className="col-5">
                                <div className="text-xs text-color-secondary">End</div>
                                <div className="font-medium">{supportAgreementEndDateTemplate(rowData)}</div>
                            </div>
                            <div className="col-12">
                                <div className="text-xs text-color-secondary">Client</div>
                                <div className="font-medium" style={{ wordBreak: 'break-word' }}>{rowData.supportAgreementClientName || '---'}</div>
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-content-between align-items-center pt-2 border-top-1 surface-border">
                        <div>
                            <div className="text-xs text-color-secondary">Handover</div>
                            <div className="font-medium">{handoverDateTemplate(rowData)}</div>
                        </div>
                        <div>
                            {actionBodyTemplate(rowData)}
                        </div>
                    </div>

                </div>
            </div>
        );
    };
    
    const mobileFilterDialogFooter = (
        <div className="flex justify-content-between">
            <Button label="Reset" icon="pi pi-times" text severity="secondary" onClick={resetMobileFilters} />
            <div className="flex gap-2">
                <Button label="Cancel" icon="pi pi-times" outlined onClick={() => setMobileFilterDialog(false)} />
                <Button label="Apply" icon="pi pi-check" onClick={applyMobileFilters} />
            </div>
        </div>
    );

    const actionBodyTemplate = (rowData) => {
        return (
            <React.Fragment>
                <Button icon="pi pi-pencil" rounded outlined className="mr-2" onClick={() => editAgreement(rowData)} />
                <Button icon="pi pi-trash" rounded outlined severity="danger" onClick={() => confirmDeleteAgreement(rowData)} />
            </React.Fragment>
        );
    };

    const rightToolbarTemplate = () => {
        return (
            <div className="flex flex-wrap gap-2">
                {/* <Button icon="pi pi-refresh" rounded raised onClick={()=>getAgreements()} /> */}
                <Button label="Export" icon="pi pi-file-export" outlined onClick={() => exportDataTableCsv(dt, 'agreements')} />
            </div>
        );
    };

    const leftToolbarTemplate = () => {
        return (
            <div className="flex flex-wrap gap-2">
                <Button label="New" icon="pi pi-plus" severity="success" onClick={openNew} />
            </div>
        );
    };

    const centerToolbarTemplate = () => {
        return (
            mobileView ? 
            <Card title="" style={{padding: '0px !important'}}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                <p className="m-0">IA - Installation Agreement</p>
                <p className="m-0">GA - Guarantee Agreement</p>
                <p className="m-0">SA - Service Agreement</p>
                </div>
            </Card>          
            :
            <Card title="" style={{padding: '0px !important'}}>
                <p className="m-0">
                IA - Installation Agreement, GA - Guarantee Agreement, SA - Service Agreement
                </p>
            </Card>
        );
    };
    const agreementDialogFooter = (
        <React.Fragment>
            <Button label="Cancel" icon="pi pi-times" outlined onClick={hideDialog} />
            <Button label="Save" icon="pi pi-check" onClick={saveAgreement} />
        </React.Fragment>
    );
    const deleteAgreementDialogFooter = (
        <React.Fragment>
            <Button label="No" icon="pi pi-times" outlined onClick={hideDeleteAgreementDialog} />
            <Button label="Yes" icon="pi pi-check" severity="danger" onClick={deleteAgreementItem} />
        </React.Fragment>
    );

    const dataView = useMemo(() => {
        return (
            <div className="card">
                <Toolbar className="mb-3" left={() => mobileFilterBar} center={centerToolbarTemplate} />
                <DataView
                    value={filteredAgreements}
                    itemTemplate={cardTemplate}
                    paginator
                    rows={10}
                />
            </div>
        );
    }, [filteredAgreements, mobileFilters, mobileFilterDialog, createAgreementSuccess, updateAgreementSuccess]);

    const dataTable = useMemo(() => {
        return (
            <div className="card">
                <Toolbar className="mb-4" left={leftToolbarTemplate} center={centerToolbarTemplate}  right={rightToolbarTemplate}> </Toolbar>

                <div ref={topScrollRef} className="systems-table-top-scroll">
                    <div ref={topScrollContentRef} style={{ height: 1 }} />
                </div>
                <div ref={tableWrapperRef}>
                <DataTable ref={dt} value={agreements} sortField="ID" sortOrder={-1}
                        dataKey="ID"  paginator rows={25} rowsPerPageOptions={[10, 25, 50, 100]}
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} items" globalFilter={globalFilter} 
                        filters={filters} filterDisplay="row" stripedRows size='small' resizableColumns
                        exportFilename="agreements"
                        exportFunction={(e) => {
                            const dateFields = [
                                'installationAgreementDate', 'handoverDate', 'guaranteeAgreementDate',
                                'guaranteeAgreementEndDate', 'supportAgreementDate', 'supportAgreementEndDate',
                            ];
                            if (dateFields.includes(e.field) && e.data) {
                                return new Date(e.data).toLocaleDateString('sv-SE');
                            }
                            return e.data;
                        }} >
                <Column exportable={false}></Column>
                <Column                    
                    field="ID" 
                    header="ID"
                    style={{maxWidth: '45px'}}
                    sortable>
                </Column>
                <Column 
                    field="systemName" 
                    header="System"
                    body={systemNameTemplate}
                    sortable
                    filter filterPlaceholder=""
                    showFilterMenu={false}>
                </Column>
                <Column 
                    field="installationAgreementNumber" 
                    header="IA Number"
                    style={{maxWidth:'180px'}}
                    sortable>
                </Column>
                <Column 
                    field="installationAgreementClientName" 
                    header="IA Client"
                    style={{maxWidth:'180px'}}
                    sortable
                    filter filterPlaceholder=""
                    showFilterMenu={false}>
                </Column>
                <Column 
                    field="installationAgreementDate" 
                    header="IA Date"
                    body={installationAgreementDateTemplate}
                    style={dateStyle}
                    sortable>                
                </Column>
                <Column 
                    field="handoverDate" 
                    header="Handover"
                    body={handoverDateTemplate}
                    style={dateStyle}
                    sortable>
                </Column>
                <Column 
                    field="guaranteeAgreementNumber" 
                    header="GA Number"
                    style={{maxWidth:'180px'}}
                    sortable>                
                </Column>
                <Column 
                    field="guaranteeAgreementClientName" 
                    header="GA Client"
                    style={{maxWidth:'180px'}}
                    sortable
                    filter filterPlaceholder=""
                    showFilterMenu={false}>
                </Column>
                <Column 
                    field="guaranteeAgreementDate" 
                    header="GA Date"
                    body={guaranteeAgreementDateTemplate}
                    style={dateStyle}
                    sortable>                
                </Column>
                <Column 
                    field="guaranteeAgreementEndDate" 
                    header="GA End Date"
                    body={guaranteeAgreementEndDateTemplate}
                    sortable>                
                </Column>
                <Column 
                    field="baseGuaranteeTime" 
                    header="GA Time"
                    sortable>                
                </Column>
                <Column 
                    field="supportAgreementNumber" 
                    header="SA Number"
                    style={{maxWidth:'180px'}}
                    sortable>                
                </Column>
                <Column 
                    field="supportAgreementClientName" 
                    header="SA Client"
                    style={{maxWidth:'180px'}}
                    sortable
                    filter filterPlaceholder=""
                    showFilterMenu={false}>
                </Column>
                <Column 
                    field="supportAgreementDate" 
                    header="SA Date" 
                    body={supportAgreementDateTemplate} 
                    style={dateStyle}
                    sortable>                
                </Column>
                <Column 
                    field="supportAgreementEndDate" 
                    header="SA End Date"
                    body={supportAgreementEndDateTemplate}
                    sortable>                
                </Column>
                <Column body={actionBodyTemplate} exportable={false} style={{ minWidth: '12rem', maxWidth: '24rem' }}></Column>
                </DataTable>
                </div>
            </div>
        );
    }, [agreements]);
    console.log('mobile view', mobileView);
    return (
        <div>
            { !!dbAgreements && (mobileView ? dataView : dataTable) }

            <Dialog visible={agreementDialog} 
                    style={{ width: mobileView ? '100%' : '32rem' }} 
                    breakpoints={{ '960px': '75vw', '641px': '90vw' }} 
                    modal className="p-fluid" footer={agreementDialogFooter} onHide={hideDialog} 
                    header={agreement.ID ? 'Edit item #' + agreement.ID : 'Add new item'}
                    >
                <div className="field">
                    <label className="mb-3 font-bold">System</label>
                    <Dropdown value={ agreement.systemName } onChange={(e) => onInputChange(e, 'systemName')} options={systemNames} optionLabel="name" 
                        filter placeholder={agreement.systemName ?? (systemNames.length ? "Select system" :"No available systems")} className="w-full md:w-28rem" invalid={!agreement.systemName} disabled={!!agreement.ID || !systemNames.length}/>
                </div>
                <div className="field">
                    <label className="mb-3 font-bold">Installation Agreement Number</label>
                    <InputText value={agreement.installationAgreementNumber} maxLength={200} onChange={(e) => onInputChange(e, 'installationAgreementNumber')} />
                </div> 
                <div className="field">
                    <label className="mb-3 font-bold">Installation Agreement Client</label>
                    <Dropdown value={agreement.installationAgreementClientId ?? null} onChange={(e) => onInputChange(e, 'installationAgreementClientId')} options={clientOptions}
                        filter showClear placeholder="Select client" emptyMessage="No clients" className="w-full md:w-28rem" />
                </div>
                <div className="formgrid grid">
                    <div className="field col">
                        <label className="mb-3 font-bold">Installation Agreement Date</label>
                        <Calendar value={new Date(agreement.installationAgreementDate)} onChange={(e) => onInputChange(e, 'installationAgreementDate')} dateFormat="yy-mm-dd" />
                    </div>
                    <div className="field col">
                        <label className="mb-3 font-bold">Handover Date</label>
                        <Calendar value={new Date(agreement.handoverDate)} onChange={(e) => onInputChange(e, 'handoverDate')} dateFormat="yy-mm-dd" />
                    </div>
                </div>
                <div className="field">
                    <label className="mb-3 font-bold">Guarantee Agreement Number</label>
                    <InputText value={agreement.guaranteeAgreementNumber} maxLength={200} onChange={(e) => onInputChange(e, 'guaranteeAgreementNumber')} />
                </div>
                <div className="field">
                    <label className="mb-3 font-bold">Guarantee Agreement Client</label>
                    <Dropdown value={agreement.guaranteeAgreementClientId ?? null} onChange={(e) => onInputChange(e, 'guaranteeAgreementClientId')} options={clientOptions}
                        filter showClear placeholder="Select client" emptyMessage="No clients" className="w-full md:w-28rem" />
                </div>
                <div className="formgrid grid">
                    <div className="field col">
                        <label className="mb-3 font-bold">Guarantee Agreement Date</label>
                        <Calendar value={new Date(agreement.guaranteeAgreementDate)} onChange={(e) => onInputChange(e, 'guaranteeAgreementDate')} dateFormat="yy-mm-dd" />
                    </div>
                    <div className="field col">
                        <label className="mb-3 font-bold">Guarantee Agreement End Date</label>
                        <Calendar value={new Date(agreement.guaranteeAgreementEndDate)} onChange={(e) => onInputChange(e, 'guaranteeAgreementEndDate')} dateFormat="yy-mm-dd" />
                    </div>
                    <div className="field col">
                        <label className="mb-3 font-bold">Base Guarantee Time</label>
                        <InputText  value={agreement.baseGuaranteeTime ?? ''} keyfilter="int" maxLength={2} onChange={(e) => onInputChange(e, 'baseGuaranteeTime')}/>
                    </div>
                </div>
                <div className="field">
                    <label className="mb-3 font-bold">Support Agreement Number</label>
                    <InputText value={agreement.supportAgreementNumber} maxLength={200} onChange={(e) => onInputChange(e, 'supportAgreementNumber')} />
                </div>
                <div className="field">
                    <label className="mb-3 font-bold">Support Agreement Client</label>
                    <Dropdown value={agreement.supportAgreementClientId ?? null} onChange={(e) => onInputChange(e, 'supportAgreementClientId')} options={clientOptions}
                        filter showClear placeholder="Select client" emptyMessage="No clients" className="w-full md:w-28rem" />
                </div>
                <div className="formgrid grid">
                    <div className="field col">
                        <label className="mb-3 font-bold">Support Agreement Date</label>
                        <Calendar value={new Date(agreement.supportAgreementDate)} onChange={(e) => onInputChange(e, 'supportAgreementDate')} dateFormat="yy-mm-dd" />
                    </div>
                    <div className="field col">
                        <label className="mb-3 font-bold">Support Agreement End Date</label>
                        <Calendar value={new Date(agreement.supportAgreementEndDate)} onChange={(e) => onInputChange(e, 'supportAgreementEndDate')} dateFormat="yy-mm-dd" />
                    </div>
                </div>
            </Dialog>

            <Dialog visible={deleteAgreementDialog} style={{ width: '32rem' }} breakpoints={{ '960px': '75vw', '641px': '90vw' }} header="Confirm" modal footer={deleteAgreementDialogFooter} onHide={hideDeleteAgreementDialog}>
                <div className="confirmation-content">
                    <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
                    {agreement && (
                        <span>
                            Are you sure you want to delete this item (#{agreement.ID})?
                        </span>
                    )}
                </div>
            </Dialog>

            <Dialog
                visible={mobileFilterDialog}
                onHide={() => setMobileFilterDialog(false)}
                header="Filter & Sort"
                style={{ width: '95vw' }}
                footer={mobileFilterDialogFooter}
                modal
                className="p-fluid"
            >
                <div className="mb-4">
                    <label className="font-bold mb-2 block">Sort by</label>
                    <div className="flex gap-2">
                        <Dropdown
                            value={pendingSortField}
                            options={sortOptions}
                            onChange={(e) => setPendingSortField(e.value)}
                            className="flex-1"
                        />
                        <Button
                            icon={pendingSortOrder === 1 ? 'pi pi-sort-amount-up' : 'pi pi-sort-amount-down'}
                            outlined
                            onClick={() => setPendingSortOrder(prev => prev === 1 ? -1 : 1)}
                            tooltip={pendingSortOrder === 1 ? 'Ascending' : 'Descending'}
                        />
                    </div>
                </div>

                <div className="mb-3">
                    <label className="text-sm text-color-secondary mb-1 block">IA Number</label>
                    <InputText
                        value={pendingFilters.installationAgreementNumber}
                        onChange={(e) => setPendingFilters(prev => ({ ...prev, installationAgreementNumber: e.target.value }))}
                        placeholder="Search..."
                    />
                </div>
                <div className="mb-3">
                    <label className="text-sm text-color-secondary mb-1 block">GA Number</label>
                    <InputText
                        value={pendingFilters.guaranteeAgreementNumber}
                        onChange={(e) => setPendingFilters(prev => ({ ...prev, guaranteeAgreementNumber: e.target.value }))}
                        placeholder="Search..."
                    />
                </div>
                <div className="mb-3">
                    <label className="text-sm text-color-secondary mb-1 block">GA End Date</label>
                    <Dropdown
                        value={pendingFilters.expiredGuarantee}
                        options={[
                            { label: 'Expired', value: true },
                            { label: 'Active', value: false },
                        ]}
                        onChange={(e) => setPendingFilters(prev => ({ ...prev, expiredGuarantee: e.value }))}
                        placeholder="Any" showClear className="w-full"
                    />
                </div>
                <div className="mb-3">
                    <label className="text-sm text-color-secondary mb-1 block">SA Number</label>
                    <InputText
                        value={pendingFilters.supportAgreementNumber}
                        onChange={(e) => setPendingFilters(prev => ({ ...prev, supportAgreementNumber: e.target.value }))}
                        placeholder="Search..."
                    />
                </div>
                <div className="mb-3">
                    <label className="text-sm text-color-secondary mb-1 block">SA End Date</label>
                    <Dropdown
                        value={pendingFilters.expiredSupport}
                        options={[
                            { label: 'Expired', value: true },
                            { label: 'Active', value: false },
                        ]}
                        onChange={(e) => setPendingFilters(prev => ({ ...prev, expiredSupport: e.value }))}
                        placeholder="Any" showClear className="w-full"
                    />
                </div>
            </Dialog>


            <Snackbar
                close
                closeNotification={() => closeUpdateAgreementSuccessNotification()}
                color='success'
                message={'Agreement updated'}
                open={updateAgreementSuccess}
                place='tc'
            />
            <Snackbar
                close
                closeNotification={() => closeUpdateAgreementFailNotification()}
                color='danger'
                message={'Update error'}
                open={updateAgreementFail}
                place='tc'
            />
            <Snackbar
                close
                closeNotification={() => closeCreateAgreementSuccessNotification()}
                color='success'
                message={'Agreement created'}
                open={createAgreementSuccess}
                place='tc'
            />
            <Snackbar
                close
                closeNotification={() => closeCreateAgreementFailNotification()}
                color='danger'
                message={'Create error'}
                open={createAgreementFail}
                place='tc'
            />
        </div>
    );
}


const mapStateToProps = (state) => {
	return {
		systems: state.dashboard.systemsVeryShort,
		mobileView: state.dashboard.mobileView,
		dbAgreements: state.dashboard.agreements,
        updateAgreementSuccess: state.dashboard.updateAgreementSuccess,
        updateAgreementFail: state.dashboard.updateAgreementFail,
        createAgreementSuccess: state.dashboard.createAgreementSuccess,
        createAgreementFail: state.dashboard.createAgreementFail,
        clients: state.dashboard.clients
	};
};

const actions = {
	getSystemsVeryShort,
    getAgreements,
    updateAgreement,
    addAgreement,
    deleteAgreement,
    closeUpdateAgreementSuccessNotification,
    closeUpdateAgreementFailNotification,
    closeCreateAgreementSuccessNotification,
    closeCreateAgreementFailNotification,
    getClients
};

export default connect(mapStateToProps, actions)(Agreements);
